import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatarData } from '../../../Funcoes/FormatarData';
import style from './AuditItens.module.css';

import classNames from 'classnames';
import styled from 'styled-components';
import { FiEdit2, FiPaperclip, FiTrash2 } from 'react-icons/fi';
import { getItensByNorma } from '../../../Funcoes/GetItensByNorma';
import SortableTable from '../../Utils/SortableTable';
import useApi from '../../../hooks/useApi';
import { useToast } from '../../../contexts/ToastContext';
import GenericHeader from '../../GenericHeader';
import Filters from '../../Utils/Filters';
import MultiselectCheck from '../../Utils/MultiselectCheck';

const PAGE_SIZE = 100;
const SCROLL_THRESHOLD = 40;

const Statistics = styled.div`
    width: 280px;
    flex-shrink: 0;
    background-color: #f3f3f3;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 12px;
    padding: 10px;
    box-sizing: border-box;

    @media (max-width: 768px) {
        width: 100%;
    }
`;

const StatisticsResumo = styled(Statistics)`
    width: auto;
    min-width: 220px;
    max-width: 300px;
    display: flex;
    align-items: stretch;
`;

const ConfirmOverlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: 100000;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.45);
`;

const ConfirmBox = styled.div`
    width: min(350px, 92%);
    background-color: #fff;
    border-radius: 8px;
    padding: 20px 16px 16px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const ConfirmTitle = styled.p`
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: #1f2937;
    text-align: center;
    line-height: 1.4;
`;

const ConfirmActions = styled.div`
    display: flex;
    gap: 10px;
    margin-top: 20px;
    width: 100%;
`;

const ConfirmButton = styled.button`
    flex: 1;
    height: 36px;
    border: none;
    border-radius: 8px;
    font-weight: 700;
    font-size: 12px;
    cursor: pointer;
    background-color: ${(p) => (p.$primary ? '#b91c1c' : '#e5e7eb')};
    color: ${(p) => (p.$primary ? '#fff' : '#1f2937')};

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const SETORES = [
    'ADM',
    'ALMOXARIFADO/MATERIAIS',
    'ALTA DIREÇÃO',
    'ANDAIMES',
    'COMERCIAL',
    'CONTABILIDADE',
    'CONTROLE DE OBRAS',
    'ELÉTRICA',
    'ENGENHARIA E ORÇAMENTOS',
    'FABRICAÇÃO (PIPE SHOP)',
    'FINANCEIRO',
    'FISCAL',
    'IÇAMENTO E MOVIMENTAÇÃO DE CARGA',
    'INSTRUMENTAÇÃO',
    'ISOLAMENTO TÉRMICO',
    'JURÍDICO',
    'LOGÍSTICA',
    'MANUTENÇÃO MECÂNICA',
    'MECÂNICA',
    'PINTURA',
    'PLANEJAMENTO / GERENCIAMENTO DE OBRAS',
    'QUALIDADE',
    'RECURSOS HUMANOS',
    'SGI - SISTEMA DE GESTÃO INTEGRADO',
    'SSMA',
    'SUPRIMENTOS',
    'T.I.',
    'TUBULAÇÃO',
];

const OPCOES_NORMA = [
    { value: 'ISO 9001:2015', label: 'ISO 9001:2015' },
    { value: 'ISO 14001:2015', label: 'ISO 14001:2015' },
    { value: 'ISO 45001:2018', label: 'ISO 45001:2018' },
];

const OPCOES_CONFORMIDADE = [
    { value: 'C', label: 'C' },
    { value: 'NC', label: 'NC' },
    { value: 'NA', label: 'NA' },
    { value: 'OM', label: 'OM' },
];

const AuditIntens = ({ itens, itensToFilter, openEdit, handleFilter, onItemDeleted }) => {

    const api = useApi();
    const { notifySuccess, notifyError } = useToast();

    const [confirmDeleteItem, setConfirmDeleteItem] = useState(null);
    const [deletingItem, setDeletingItem] = useState(false);

    const lista = useMemo(
        () => (Array.isArray(itens) ? itens : []),
        [itens]
    );

    const fonteFiltro = useMemo(
        () => (Array.isArray(itensToFilter) ? itensToFilter : []),
        [itensToFilter]
    );

    const [selectedNorma, setSelectedNorma] = useState([]);
    const [itensNorma, setItensNorma] = useState([]);
    const [selectedItens, setSelectedItens] = useState([]);
    const [selectedAuditores, setSelectedAuditores] = useState([]);
    const [selectedAuditados, setSelectedAuditados] = useState([]);
    const [selectedSetores, setSelectedSetores] = useState([]);
    const [selectedConf, setSelectedConf] = useState([]);

    const opcoesAuditores = useMemo(
        () =>
            [...new Set(fonteFiltro.map((obj) => obj.auditor).filter(Boolean))]
                .sort()
                .map((opt) => ({ value: opt, label: opt })),
        [fonteFiltro]
    );

    const opcoesAuditados = useMemo(
        () =>
            [...new Set(fonteFiltro.map((obj) => obj.auditado).filter(Boolean))]
                .sort()
                .map((opt) => ({ value: opt, label: opt })),
        [fonteFiltro]
    );

    const opcoesSetores = useMemo(
        () => SETORES.map((opt) => ({ value: opt, label: opt })),
        []
    );

    const opcoesItens = useMemo(
        () => itensNorma.map((opt) => ({ value: opt, label: opt })),
        [itensNorma]
    );

    const { itensC, itensNC, itensNA, itensOM, percentage } = useMemo(() => {
        const c = lista.filter((obj) => obj.conformidade === 'C').length;
        const nc = lista.filter((obj) => obj.conformidade === 'NC').length;
        const na = lista.filter((obj) => obj.conformidade === 'NA').length;
        const om = lista.filter((obj) => obj.conformidade === 'OM').length;
        const total = lista.length;

        const conformidades = lista.reduce(
            (acc, item) =>
                item.conformidade === 'C' ||
                item.conformidade === 'NA' ||
                item.conformidade === 'OM'
                    ? acc + 1
                    : acc,
            0
        );

        const pct = total > 0 ? (conformidades / total) * 100 : 0;

        return {
            itensC: c,
            itensNC: nc,
            itensNA: na,
            itensOM: om,
            percentage: pct,
        };
    }, [lista]);

    const classeCircle = classNames(style.circlePercent, {
        [style.lowPercent]: percentage <= 50,
        [style.mediumPercent]: percentage > 50 && percentage <= 75,
        [style.highPercent]: percentage > 75,
    });

    useEffect(() => {
        const normaValues = selectedNorma.map((obj) => obj.value);

        const itensList = Array.from(
            new Set(
                normaValues.flatMap((norma) => getItensByNorma(norma) || [])
            )
        ).sort((a, b) => a.localeCompare(b));

        setItensNorma(itensList);
        setSelectedItens([]);
    }, [selectedNorma]);

    useEffect(() => {
        handleFilter(
            selectedNorma,
            selectedItens,
            selectedAuditores,
            selectedAuditados,
            selectedSetores,
            selectedConf
        );
    }, [
        selectedNorma,
        selectedItens,
        selectedAuditores,
        selectedAuditados,
        selectedSetores,
        selectedConf,
        handleFilter,
    ]);

    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const tabelaRef = useRef(null);

    useEffect(() => {
        setVisibleCount(PAGE_SIZE);
        if (tabelaRef.current) {
            tabelaRef.current.scrollTop = 0;
        }
    }, [lista]);

    const loadMoreRows = useCallback(() => {
        setVisibleCount((prev) => {
            const total = lista.length;
            if (prev >= total) return prev;
            return Math.min(prev + PAGE_SIZE, total);
        });
    }, [lista.length]);

    const openDeleteConfirm = useCallback((row) => {
        setConfirmDeleteItem({
            id: row.id,
            label: row.subitem
                ? `${row.id_count} — ${row.subitem}`
                : `ID ${row.id_count}`,
        });
    }, []);

    const handleCancelDelete = useCallback(() => {
        if (deletingItem) return;
        setConfirmDeleteItem(null);
    }, [deletingItem]);

    const handleConfirmDelete = useCallback(async () => {
        if (!confirmDeleteItem?.id || deletingItem) return;

        setDeletingItem(true);

        try {
            await api.post('/Painel/API/AuditInterna/DeleteItem/', {
                id: confirmDeleteItem.id,
            });

            notifySuccess('Item removido com sucesso.');
            setConfirmDeleteItem(null);

            if (typeof onItemDeleted === 'function') {
                await onItemDeleted();
            }
        } catch (error) {
            const msg = error.response?.data?.message ?? 'Erro ao remover o item.';
            notifyError(msg);
        } finally {
            setDeletingItem(false);
        }
    }, [api, confirmDeleteItem, deletingItem, notifyError, notifySuccess, onItemDeleted]);

    const columns = useMemo(
        () => [
            {
                id: 'id_count',
                header: 'ID',
                accessor: 'id_count',
                sortable: true,
                width: '70px',
                align: 'center',
            },
            {
                id: 'subitem',
                header: 'ITEM',
                accessor: 'subitem',
                sortable: true,
                align: 'left',
            },
            {
                id: 'norma',
                header: 'NORMA',
                accessor: 'norma',
                sortable: true,
            },
            {
                id: 'data',
                header: 'DATA',
                accessor: 'data',
                sortable: true,
                width: '160px',
                align: 'center',
                render: (value) => (value ? formatarData(value, true) : '-'),
            },
            {
                id: 'auditado',
                header: 'AUDITADO',
                accessor: 'auditado',
                sortable: true,
            },
            {
                id: 'auditor',
                header: 'AUDITOR',
                accessor: 'auditor',
                sortable: true,
            },
            {
                id: 'setor',
                header: 'SETOR',
                accessor: 'setor',
                sortable: true,
            },
            {
                id: 'conformidade',
                header: 'CONFORMIDADE',
                accessor: 'conformidade',
                sortable: true,
                width: '120px',
                align: 'center',
            },
            {
                id: 'anexos',
                header: 'ANEXOS',
                accessor: 'anexos',
                sortable: true,
                width: '90px',
                align: 'center',
                render: (value) => {
                    const temAnexo =
                        value === '*' ||
                        (typeof value === 'number' && value > 0) ||
                        (Array.isArray(value) && value.length > 0);

                    if (!temAnexo) return '-';

                    return (
                        <span
                            className={style.anexoIndicador}
                            title="Possui anexos"
                            aria-label="Possui anexos"
                        >
                            <FiPaperclip size={16} />
                        </span>
                    );
                },
                sortValue: (row) => (row.anexos === '*' ? 1 : 0),
            },
            {
                id: 'acoes',
                header: 'AÇÕES',
                sortable: false,
                width: '130px',
                align: 'center',
                render: (_, row) => (
                    <div className={style.acoesCelula}>
                        <button
                            type="button"
                            className={style.botaoAcao}
                            title="Editar item"
                            onClick={(e) => {
                                e.stopPropagation();
                                openEdit(row.id);
                            }}
                        >
                            <FiEdit2 size={14} />
                        </button>
                        <button
                            type="button"
                            className={`${style.botaoAcao} ${style.botaoAcaoDanger}`}
                            title="Excluir item"
                            disabled={deletingItem}
                            onClick={(e) => {
                                e.stopPropagation();
                                openDeleteConfirm(row);
                            }}
                        >
                            <FiTrash2 size={14} />
                        </button>
                    </div>
                ),
            },
        ],
        [deletingItem, openDeleteConfirm, openEdit]
    );

    return (
        <div className={style.container}>
            <GenericHeader responsive>
                <Filters columns={3}>
                    <MultiselectCheck
                        options={OPCOES_NORMA}
                        selectedOptions={selectedNorma}
                        setSelectedOptions={setSelectedNorma}
                        placeholder="NORMA..."
                    />
                    <MultiselectCheck
                        options={opcoesItens}
                        selectedOptions={selectedItens}
                        setSelectedOptions={setSelectedItens}
                        placeholder="ITEM..."
                    />
                    <MultiselectCheck
                        options={opcoesAuditores}
                        selectedOptions={selectedAuditores}
                        setSelectedOptions={setSelectedAuditores}
                        placeholder="AUDITOR..."
                    />
                    <MultiselectCheck
                        options={opcoesAuditados}
                        selectedOptions={selectedAuditados}
                        setSelectedOptions={setSelectedAuditados}
                        placeholder="AUDITADO..."
                    />
                    <MultiselectCheck
                        options={opcoesSetores}
                        selectedOptions={selectedSetores}
                        setSelectedOptions={setSelectedSetores}
                        placeholder="SETOR..."
                    />
                    <MultiselectCheck
                        options={OPCOES_CONFORMIDADE}
                        selectedOptions={selectedConf}
                        setSelectedOptions={setSelectedConf}
                        placeholder="CONFORMIDADE..."
                    />
                </Filters>

                <StatisticsResumo>
                    <div className={style.resumoInner}>
                        <div className={style.statsLeft}>
                            <div className={style.confContainer}>{`C:  ${itensC}`}</div>
                            <div className={style.confContainer}>{`NC: ${itensNC}`}</div>
                            <div className={style.confContainer}>{`NA: ${itensNA}`}</div>
                            <div className={style.confContainer}>{`OM: ${itensOM}`}</div>
                        </div>
                        <div className={style.statsSeparator} />
                        <div className={style.statsRight}>
                            <div className={classeCircle}>
                                <p>{percentage.toFixed(2)}%</p>
                            </div>
                        </div>
                    </div>
                </StatisticsResumo>
            </GenericHeader>

            <div className={style.tableFlexFill}>
                <SortableTable
                    ref={tabelaRef}
                    columns={columns}
                    data={lista}
                    limit={visibleCount}
                    onScrollReachEnd={loadMoreRows}
                    scrollReachThreshold={SCROLL_THRESHOLD}
                    emptyText="Nenhum item encontrado."
                    initialSort={{ key: 'id_count', direction: 'desc' }}
                    getRowId={(row) => row.id}
                />
            </div>

            {confirmDeleteItem && (
                <ConfirmOverlay
                    onClick={(e) => {
                        e.stopPropagation();
                        if (e.target === e.currentTarget) {
                            handleCancelDelete();
                        }
                    }}
                >
                    <ConfirmBox onClick={(e) => e.stopPropagation()}>
                        <ConfirmTitle>
                            Deseja excluir o item &quot;{confirmDeleteItem.label}&quot;?
                        </ConfirmTitle>
                        <ConfirmActions>
                            <ConfirmButton
                                type="button"
                                onClick={handleCancelDelete}
                                disabled={deletingItem}
                            >
                                NÃO
                            </ConfirmButton>
                            <ConfirmButton
                                type="button"
                                $primary
                                onClick={handleConfirmDelete}
                                disabled={deletingItem}
                            >
                                {deletingItem ? 'EXCLUINDO…' : 'SIM'}
                            </ConfirmButton>
                        </ConfirmActions>
                    </ConfirmBox>
                </ConfirmOverlay>
            )}
        </div>
    );

};

export default AuditIntens;
